import { useSyncExternalStore } from 'react';

/** Identity of the salon an agency member just picked, shown while the switch completes. */
export interface PendingAgencySwitch {
  /** Signed-in user who started the switch; the entry is invisible to anyone else. */
  userId: string;
  shortCode: string;
  name: string;
  logoUrl: string | null;
}

let pendingSwitch: PendingAgencySwitch | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return pendingSwitch;
}

/**
 * Module-level (not URL/search-param) handoff from the salon switcher to the
 * switching frame: the switcher records the target salon's name + logo on
 * select, the frame reads it to paint the new salon in the ribbon immediately.
 */
export function setPendingAgencySwitch(next: PendingAgencySwitch) {
  pendingSwitch = next;
  emit();
}

/**
 * Clears the pending switch once it completed (or failed). Passing a shortCode
 * clears only that switch, so finishing an older switch never wipes a newer one.
 */
export function clearPendingAgencySwitch(shortCode?: string) {
  if (!pendingSwitch) return;
  if (shortCode !== undefined && pendingSwitch.shortCode !== shortCode) return;
  pendingSwitch = null;
  emit();
}

/**
 * The pending switch into `shortCode` started by `userId`, or `null`. Matching
 * both means a stale entry can never paint the wrong salon or leak to another
 * user; callers fall back to the plain loader on `null`.
 */
export function usePendingAgencySwitch(
  shortCode: string,
  userId: string | null | undefined
): PendingAgencySwitch | null {
  const pending = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!pending || !userId) return null;
  return pending.shortCode === shortCode && pending.userId === userId ? pending : null;
}
